import { z } from 'zod';

export const brandSchema = z.object({
  name: z.string(),
  tagline: z.string().optional(),
  colors: z.object({
    primary: z.string(),
    secondary: z.string(),
    accent: z.string(),
    text: z.string().optional(),
    background: z.string().optional(),
  }),
  fonts: z.object({
    heading: z.string(),
    body: z.string(),
  }),
  // Legacy single-logo field. Every consumer that predates the logo roles
  // below still reads it, so it stays required; new surfaces resolve a
  // role through `resolveBrandLogo` (brand-logo.ts) and only fall back here.
  logo: z.string(),
  // Optional dark-surface logo variant (reversed/white art). Consumed by
  // the admin sidebar (dark --brand-secondary background) in preference to
  // `logo`, which is typically full-color art built for light surfaces.
  // Sourced statically from clients/<slug>/config.ts or at runtime from
  // BrandKit.logo.darkModePrimarySvg via brandKitToBrand.
  logoDark: z.string().optional(),
  // Kate K13 — logo ROLES. HTHD's `logo` was a 1080x1080 square whose art
  // filled 42% of the height, so at the header's 48px the mark was ~20px
  // on every surface ("the logo is staying like super micro tiny"). The
  // fix is not CSS: a header needs the horizontal lockup, an icon or avatar
  // needs the square mark, a dark chrome needs the reversed art. Each role
  // is optional; `resolveBrandLogo` falls back to `logo` / `logoDark`.
  logoHorizontal: z.string().optional(),
  logoMark: z.string().optional(),
  logoOnDark: z.string().optional(),
});

export const verticalSchema = z.enum([
  'pet-services',
  'salon-spa',
  'fitness',
  'medical-dental',
  'professional-services',
]);

export const moduleNameSchema = z.enum([
  'agreements',
  'ai-chat',
  // Lane D (D2/D3) — ai-manager promoted from manifest-only to a real
  // ModuleDefinition (`name: ModuleName`), same promotion pattern as
  // incident-reporting (#139) and staff-scheduling (#137). Already canonical
  // as a manifest id (packages/core/modules/ai-manager/module.ts). Purely
  // additive — widens accepted module names, cannot invalidate a config.
  'ai-manager',
  'ai-monitor',
  'analytics',
  'auth',
  'badges',
  'booking',
  'compliance',
  'crm',
  'dog-profile',
  'email-campaigns',
  // #139 — incident-reporting promoted from manifest-only to a real
  // ModuleDefinition (`name: ModuleName`). Already canonical in
  // CANONICAL_MODULE_SLUGS; its absence here was a latent gap (same as
  // staff-scheduling in #137). Purely additive — widens accepted module
  // names, cannot invalidate an existing config.
  'incident-reporting',
  'loyalty',
  'messaging',
  'notifications',
  'payments',
  'reporting',
  // #427 W1 — staff-payroll promoted from manifest-only to a real
  // ModuleDefinition (`name: ModuleName`), same promotion pattern as
  // staff-scheduling (#137) / incident-reporting (#139) / ai-manager.
  // Already canonical in CANONICAL_MODULE_SLUGS. Purely additive —
  // widens accepted module names, cannot invalidate an existing config.
  'staff-payroll',
  'staff-mgmt',
  // #137 — staff-scheduling promoted from manifest-only to a real
  // ModuleDefinition (`name: ModuleName`). The slug was already canonical
  // in `CANONICAL_MODULE_SLUGS` and `INTAKE_V1_MODULE_SLUGS`; its absence
  // here was a latent gap. Purely additive — widens accepted module
  // names, cannot invalidate an existing config.
  'staff-scheduling',
  'time-attendance',
]);

export const businessSchema = z.object({
  services: z.array(z.string()),
  pricing: z.record(z.string(), z.number().int().nonnegative()),
  locations: z.array(
    z.object({
      name: z.string(),
      address: z.string(),
    }),
  ),
  // Optional explicit prefix for referral codes (e.g. "HT" → "HT-AB12CD").
  // When omitted, the auth module derives one from brand.name. Never
  // hardcoded inside modules.
  referralPrefix: z
    .string()
    .regex(/^[A-Z0-9]{2,6}$/, 'Referral prefix must be 2–6 uppercase alphanumerics')
    .optional(),
  // Tenant-configurable refund policy text rendered on the receipt page
  // (CheckoutConfirmationPage). When omitted, the customer-app falls back
  // to a generic platform default. Plain prose; no Markdown rendering.
  refundPolicy: z.string().min(1).optional(),
});

export const agreementsConfigSchema = z.object({
  // How a customer evidences assent on the sign endpoint. The demo only
  // implements typed_name today; the other modes are reserved for the
  // clients that require initial-box or drawn signatures (e.g. medical).
  // Surfaced through ctx.config so modules never hardcode UX contracts.
  signatureMode: z.enum(['typed_name', 'initials', 'drawn']).default('typed_name'),
});

export const loyaltyConfigSchema = z.object({
  // Points earned per dollar of spend (applied before serviceMultipliers).
  // NOTE: this is the STATIC seed/default. Once a tenant exists, the
  // authoritative earn rate is `TenantConfig.earnRatePointsPerDollar`
  // (admin-editable) — see packages/core/tenant/get-tenant-loyalty-config.ts.
  earnRate: z.number().positive().default(1),
  // Per-service multipliers keyed by service name from businessSchema.services.
  serviceMultipliers: z.record(z.string(), z.number().positive()).default({}),
  // #524 — the SINGLE source of truth for point valuation: cents of discount
  // per point redeemed. Used by BOTH the checkout redemption leg
  // (deriveCheckoutLegs) and the desk redemption tiers (reserveRedemption
  // derives each tier's discount as `tierPoints * redemptionValueCentsPerPoint`).
  // This collapses the historical 1¢-at-checkout / 10¢-at-desk contradiction.
  // STATIC seed/default (1¢); the authoritative value is
  // `TenantConfig.redemptionValueCentsPerPoint` (admin-editable).
  redemptionValueCentsPerPoint: z.number().int().positive().default(1),
  // Redemption ladder: which point rungs are OFFERED (keys). The map VALUES
  // are legacy and no longer drive pricing — the discount for a rung is
  // derived from `redemptionValueCentsPerPoint` (#524), so `{ '100': … }`
  // reads as "a 100-point rung is available", not "worth … cents".
  redemptionTiers: z.record(z.string(), z.number().int().positive()).default({}),
});

export const messagingConfigSchema = z.object({
  // Max characters per customer- or staff-authored message. Defaults to
  // 2,000 (the demo's limit) when omitted. Absolute ceiling lives in the
  // messaging module itself.
  maxMessageLength: z.number().int().positive().max(8_000).default(2_000),
  // Greeting the customer sees when a brand-new web_chat conversation
  // spins up. Defaults to a generic line inside the module if omitted.
  welcomeGreeting: z.string().min(1).optional(),
});

export const deploymentSchema = z.object({
  apiUrl: z.string().url(),
  customerAppUrl: z.string().url(),
  adminAppUrl: z.string().url(),
});

export const integrationsSchema = z.object({
  stripe: z
    .object({
      mode: z.enum(['live', 'test', 'simulation']),
    })
    .nullable(),
  openai: z
    .object({
      model: z.string(),
    })
    .nullable(),
  pos: z
    .object({
      provider: z.string(),
    })
    .nullable(),
});

export const prismaConfigSchema = z.object({
  // Optional path (relative to clients/<id>/) to a Prisma fragment
  // appended after the vertical's canonical schema at prepare-schema time.
  // Bounded by convention to ≤5 models; if a client outgrows this, fork
  // the vertical. See docs/SCHEMA-COMPOSITION.md.
  extensionsPath: z.string().default('prisma/extensions.prisma').optional(),
});

export const featuresConfigSchema = z.object({
  // Whether this tenant has an OPERATIONAL SMS / text-messaging program
  // (i.e. a wired Twilio delivery path). Defaults FALSE — no tenant has SMS
  // today. Customer-facing SMS copy in Privacy / Terms / Settings is gated on
  // this flag so those pages never describe a program that doesn't exist. Flip
  // to true per-tenant once SMS delivery actually ships (issue #529).
  sms: z.boolean().default(false),
  // #1212 texting B2a — PRE-LAUNCH consent capture. When on (and `sms` is
  // still off), the client sign-up page shows the two unchecked texting
  // boxes, existing clients get the one-time sign-in prompt, Notifications
  // lets a client turn texts on/off, and Privacy/Terms carry the SMS terms —
  // but NOTHING texts anyone. It exists so a tenant can collect real,
  // provable consent (and take the carrier-registration screenshot) before
  // its texting number is approved. `sms` implies everything this does.
  // Defaults FALSE; HTHD turned it on 2026-10-14 for its toll-free
  // registration. Readers use `smsConsentCaptureOn(config)` in the apps,
  // never this field alone.
  smsConsentCapture: z.boolean().default(false),
  // #1212 texting B2a — whether consent capture also offers the MARKETING
  // ("offers and news") texting box. OFF by default and independent of the
  // two flags above: a tenant's carrier registration describes its texting
  // use case, and a toll-free / 10DLC submission that says "no marketing"
  // is rejected when the screenshotted opt-in page also offers marketing
  // texts. Turn it on only once the tenant registers a marketing use case.
  // Readers use `smsMarketingConsentOn(config)`, never this field alone.
  smsMarketingConsent: z.boolean().default(false),
  // #716 — whether tapping Check In opens the care-capture sheet
  // (belongings / feeding / meds, confirm-or-edit, always skippable).
  //
  // Defaults FALSE and NO client config sets it, which is the feature's
  // dark-launch guarantee: the server columns are all nullable and the run
  // card falls back to its pre-#716 blank-line stationery when nothing has
  // been captured, so with this flag off a tenant cannot tell #716 shipped.
  // Flip it per-tenant once the pre-filled-vs-blank run-card question is
  // settled with the owners (Ted/Julian originally asked for BLANK cards;
  // Kylie asked for PRE-FILLED — "pre-filled when data exists, blank lines
  // otherwise" is the rule that satisfies both, and it needs their yes).
  checkInCapture: z.boolean().default(false),
  // Ted Item 34 (HTHD, 10-07: "extremely important") — after a successful
  // check-in, offer to print that dog's run card right there, so the desk
  // never has to go looking for the button. Defaults TRUE: every tenant gets
  // the prompt (it is one tap to dismiss); a client config sets it false to
  // opt out. Readers use `config.features?.runCardPromptOnCheckIn !== false`
  // so a config that omits `features` entirely still gets the prompt.
  runCardPromptOnCheckIn: z.boolean().default(true),
});

export const clientConfigSchema = z.object({
  id: z.string().regex(/^[a-z0-9-]+$/, 'lowercase kebab-case only'),
  brand: brandSchema,
  vertical: verticalSchema,
  modules: z.array(moduleNameSchema),
  business: businessSchema,
  deployment: deploymentSchema,
  integrations: integrationsSchema,
  // Feature flags. Optional — a config that omits `features` (or omits a flag
  // within it) reads as disabled. Consumers use `config.features?.sms`.
  features: featuresConfigSchema.optional(),
  agreements: agreementsConfigSchema.optional(),
  loyalty: loyaltyConfigSchema.optional(),
  messaging: messagingConfigSchema.optional(),
  prisma: prismaConfigSchema.optional(),
});

export type Brand = z.infer<typeof brandSchema>;
export type Vertical = z.infer<typeof verticalSchema>;
export type ModuleName = z.infer<typeof moduleNameSchema>;
export type BusinessConfig = z.infer<typeof businessSchema>;
export type AgreementsConfig = z.infer<typeof agreementsConfigSchema>;
export type LoyaltyConfig = z.infer<typeof loyaltyConfigSchema>;
export type MessagingConfig = z.infer<typeof messagingConfigSchema>;
export type PrismaConfig = z.infer<typeof prismaConfigSchema>;
export type FeaturesConfig = z.infer<typeof featuresConfigSchema>;
export type ClientConfig = z.infer<typeof clientConfigSchema>;

/**
 * #1212 texting B2a — whether a tenant's client surfaces show SMS consent
 * capture: the sign-up boxes, the sign-in prompt, the Notifications texting
 * switches and the Privacy/Terms SMS sections. True for an operational SMS
 * program (`features.sms`) OR the pre-launch `features.smsConsentCapture`.
 * Every consent-capture surface gates on this one function, so the two flags
 * can never disagree about what the client sees. Claims that texts are
 * actually being SENT (booking summary, Settings deals) stay on
 * `features.sms` alone.
 */
export function smsConsentCaptureOn(config: Pick<ClientConfig, 'features'>): boolean {
  return config.features?.sms === true || config.features?.smsConsentCapture === true;
}

/**
 * #1212 texting B2a — whether the consent surfaces OFFER the marketing
 * texting box. Requires consent capture to be on AND the tenant's separate
 * `smsMarketingConsent` flag (default off), so a tenant whose carrier
 * registration says "no marketing" never shows an offers opt-in. A client who
 * already said yes to marketing can always still turn it off (Notifications
 * renders the switch for a granted state regardless of this function).
 */
export function smsMarketingConsentOn(config: Pick<ClientConfig, 'features'>): boolean {
  return smsConsentCaptureOn(config) && config.features?.smsMarketingConsent === true;
}
